import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import type { Dispatch, ReactElement, SetStateAction } from 'react';

import user from 'js/lib/user';

import type { ProfilePhone } from 'bundles/account-profile/components/types';
import {
  EMAIL_CONSTRAINT as isEmailValid,
  FIRSTNAME_CONSTRAINT as isFirstNameValid,
  LASTNAME_CONSTRAINT as isLastNameValid,
  PASSWORD_CONSTRAINT as isPasswordValid,
} from 'bundles/authentication/constants';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { getInputfromUserProfile, getSplitName } from 'bundles/xddp/utils/name-formatting';

type EoiOptimizationsProps = {
  firstName: string;
  setFirstName: Dispatch<SetStateAction<string>>;
  lastName: string;
  setLastName: Dispatch<SetStateAction<string>>;
  phone: Phone;
  learnerEmail: string;
  setLearnerEmail: Dispatch<SetStateAction<string>>;
  password: string;
  setPassword: Dispatch<SetStateAction<string>>;
  showError: boolean;
  setShowError: Dispatch<SetStateAction<boolean>>;
  firstNameError: string;
  lastNameError: string;
  learnerEmailError: string;
  passwordError: string;
  hasPhoneError: boolean;
  setHasPhoneError: Dispatch<SetStateAction<boolean>>;
  partnerName: string;
  slug: string;
  degreeId: string;
  responseId: string;
  setResponseId: Dispatch<SetStateAction<string>>;
  handlePhoneNumberChange: (data?: ProfilePhone) => void;
};

const EoiOptimizationsContext = createContext<EoiOptimizationsProps>({
  firstName: '',
  setFirstName: () => undefined,
  lastName: '',
  setLastName: () => undefined,
  phone: { phoneNumber: '' },
  learnerEmail: '',
  setLearnerEmail: () => undefined,
  password: '',
  setPassword: () => undefined,
  showError: false,
  setShowError: () => undefined,
  firstNameError: '',
  lastNameError: '',
  learnerEmailError: '',
  passwordError: '',
  hasPhoneError: false,
  setHasPhoneError: () => undefined,
  partnerName: '',
  slug: '',
  degreeId: '',
  responseId: '',
  setResponseId: () => undefined,
  handlePhoneNumberChange: () => undefined,
});

export const useEoiOptimizationsContext = () => useContext(EoiOptimizationsContext);

type PropsToComponent = {
  children: ReactElement;
  partnerName: string;
  slug: string;
  degreeId: string;
};

export const EoiOptimizationsContextProvider: React.FunctionComponent<PropsToComponent> = (props) => {
  const { children, partnerName, slug, degreeId } = props;
  const currentUser = user.get();
  const defaultName = getSplitName(currentUser.fullName);

  const [firstName, setFirstName] = useState<string>(defaultName.firstName || '');
  const [lastName, setLastName] = useState<string>(defaultName.lastName || '');
  const [phone, setPhone] = useState<Phone>({ phoneNumber: '' });
  const [learnerEmail, setLearnerEmail] = useState<string>(currentUser.email_address);
  const [password, setPassword] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<string>('');
  const [learnerEmailError, setLearnerEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [hasPhoneError, setHasPhoneError] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [responseId, setResponseId] = useState<string>('');

  useEffect(() => {
    if (user.isAuthenticatedUser()) {
      const fullName = getSplitName(currentUser.full_name);

      setFirstName(fullName.firstName || '');
      setLastName(fullName.lastName || '');
    }
    // Only want to check on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const error = isFirstNameValid(firstName);

    if (error && !user.isAuthenticatedUser()) {
      setFirstNameError(error);
    } else {
      setFirstNameError('');
    }
  }, [firstName]);

  useEffect(() => {
    const error = isLastNameValid(lastName);

    if (error && !user.isAuthenticatedUser()) {
      setLastNameError(error);
    } else {
      setLastNameError('');
    }
  }, [lastName]);

  useEffect(() => {
    const error = isEmailValid(learnerEmail);

    if (error) {
      setLearnerEmailError(error);
    } else {
      setLearnerEmailError('');
    }
  }, [learnerEmail]);

  useEffect(() => {
    const error = isPasswordValid(password);

    if (error) {
      setPasswordError(error);
    } else {
      setPasswordError('');
    }
  }, [password]);

  useEffect(() => {
    const fetchPhoneNumber = async () => {
      if (user.isAuthenticatedUser()) {
        const phoneResponse = await getInputfromUserProfile();
        let defaultPhone = { phoneNumber: '' } as Phone;
        if (phoneResponse.phoneNumber) {
          defaultPhone = phoneResponse.phoneNumber['org.coursera.survey.question.ShortAnswerResponse'].text as Phone;
        }

        setPhone(defaultPhone as Phone);
      } else {
        setPhone({
          phoneNumber: '',
        });
      }
    };

    fetchPhoneNumber();
  }, []);

  const handlePhoneNumberChange = (data?: ProfilePhone) => {
    if (data?.phone) {
      const phoneNumberForEoiForm = data.phone;
      setPhone(phoneNumberForEoiForm);
    }
  };

  return (
    <EoiOptimizationsContext.Provider
      value={{
        firstName,
        setFirstName,
        lastName,
        setLastName,
        phone,
        learnerEmail,
        setLearnerEmail,
        password,
        setPassword,
        showError,
        setShowError,
        firstNameError,
        lastNameError,
        learnerEmailError,
        passwordError,
        hasPhoneError,
        setHasPhoneError,
        partnerName,
        slug,
        degreeId,
        responseId,
        setResponseId,
        handlePhoneNumberChange,
      }}
    >
      {children}
    </EoiOptimizationsContext.Provider>
  );
};
